import { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Routes, Route, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../Config/axiosInstance';
import './CostControl.css';
import AddCostModal from "./AddCostModal";
import EditCostModal from "./EditCostModal";
import CostViewPage from "./CostViewPage";

function CostControl() {
  const navigate = useNavigate();
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canView, setCanView] = useState(false);
  const [rows, setRows] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedCost, setSelectedCost] = useState(null);


  // Verificare permisiuni
  useEffect(() => {
    const requiredPath = "/cost-control-administrative";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const checkPermission = (key) =>
      permissions.some(page => {
        try {
          const pagePath = new URL(page.url).pathname;
          return pagePath === requiredPath && page[key] === true;
        } catch {
          return false;
        }
      });

    setCanCreate(checkPermission('can_create'));
    setCanEdit(checkPermission('can_edit'));
    setCanDelete(checkPermission('can_delete'));
    setCanView(checkPermission('can_view'));
  }, []);

  // Funcția principală pentru încărcarea datelor
  const fetchData = async () => {
    try {
      const [
        CostControlResponse,
        projectCodesResponse,
        BudgetTypeResponse,
        ExpensesTypeResponse,
        DepartmentResponse,
        ResourceResponse,
        DepartmentResourceResponse,
        SuppliersResources,
      ] = await Promise.all([
        axiosInstance.get('/cost-administrative/'),
        axiosInstance.get('/project-codes/'),
        axiosInstance.get('/budget-type/'),
        axiosInstance.get('/expenses-type/'),
        axiosInstance.get('/departments/'),
        axiosInstance.get('/employees/'),
        axiosInstance.get('/departments/'),
        axiosInstance.get('/suppliers/')
      ]);

      const ProjectCodeMap = projectCodesResponse.data.reduce((acc, code) => {
        acc[code.id] = code.code;
        return acc;
      }, {});

      const BudgetTypeMap = BudgetTypeResponse.data.reduce((acc, budget) => {
        acc[budget.id] = budget.type;
        return acc;
      }, {});

      const ExpensesTypeMap = ExpensesTypeResponse.data.reduce((acc, expenses) => {
        acc[expenses.id] = expenses.type;
        return acc;
      }, {});

      const DepartmentMap = DepartmentResponse.data.reduce((acc, dept) => {
        acc[dept.id] = dept.name;
        return acc;
      }, {});

      const ResourceMap = ResourceResponse.data.reduce((acc, res) => {
        acc[res.id] = res.last_name + " " + res.first_name;
        return acc;
      }, {});

      const DepartmentResourceMap = DepartmentResourceResponse.data.reduce((acc, dept) => {
        acc[dept.id] = dept.name;
        return acc;
      }, {});

      const SuppliersMap = SuppliersResources.data.reduce((acc, sup) => {
        acc[sup.id] = sup.name;
        return acc;
      }, {});

      const sortedData = CostControlResponse.data.map(item => {
        const formattedPayDate = item.pay_date
          ? new Date(item.pay_date).toLocaleDateString("ro-RO")
          : "-";

        return {
          id: item.id,
          budget_type: BudgetTypeMap[item.budget_type] || "-",
          project_code: ProjectCodeMap[item.project_code] || "-",
          resource:
            ResourceMap[item.resource] ||
            DepartmentResourceMap[item.department_resource] ||
            SuppliersMap[item.supplier] ||
            "-",
          department: DepartmentMap[item.department] || "-",
          expenses_type: ExpensesTypeMap[item.expenses_type] || "-",
          pay_date: formattedPayDate,
          remarks: item.remarks,
          status: item.status,
          original: item,
        };
      }).sort((a, b) => b.id - a.id);


      setRows(sortedData);
    } catch (error) {
      console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
    }
  };

  // Rulează fetchData doar la prima încărcare
  useEffect(() => {
    fetchData();
  }, []);

  // ADD
  const handleAddCost = () => {
    fetchData();
  };

  // DELETE
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  // EDIT
  const handleEditClick = (row) => {
    setSelectedCost(row.original);
    setOpenEditModal(true);
  };

  // VIEW
  const handleViewClick = (row) => {
    navigate(`view/${row.id}`, {
      state: { costData: row.original }
    });
  };

  // UPDATE
  const handleCostUpdated = () => {
    fetchData();
  };

  // Coloane DataGrid
  const baseColumns = [
    { field: 'budget_type', headerName: 'Budget Type', flex: 1.2, minWidth: 140 },
    { field: 'project_code', headerName: 'Project Code', flex: 1.2, minWidth: 150 },
    { field: 'resource', headerName: 'Resource', flex: 1, minWidth: 120 },
    { field: 'department', headerName: 'Department', flex: 1, minWidth: 120 },
    { field: 'expenses_type', headerName: 'Expenses Type', flex: 1.2, minWidth: 120 },
    { field: 'pay_date', headerName: 'Pay Date', flex: 1, minWidth: 95 },
    { field: 'remarks', headerName: 'Remarks', flex: 1.5, minWidth: 200 },
    {
      field: 'status',
      headerName: 'Invoice Status',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const isPaid = params.value === "paid" || params.value === "Paid";
        return (
          <div className={`status-badge ${isPaid ? "status-paid" : "status-unpaid"}`}>
            <span className={`status-dot ${isPaid ? "paid" : "unpaid"}`}></span>
            {isPaid ? "Paid" : "Unpaid"}
          </div>
        );
      },
    },
  ];

  const actionColumn = {
    field: 'action',
    headerName: 'Action',
    width: 110,
    renderCell: (params) => (
      <div style={{ display: 'flex', gap: '4px' }}>
        {canView && (
          <IconButton
            onClick={() => handleViewClick(params.row)}
            sx={{ color: '#2e7d32' }}
            size="small"
            title="View Details"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        )}
        {canEdit && (
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: '#1976d2' }}
            size="small"
            title="Edit"
          >
            <EditNoteSharpIcon fontSize="small" />
          </IconButton>
        )}
        {canDelete && (
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: '#d32f2f' }}
            size="small"
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </div>
    ),
    sortable: false,
    disableColumnMenu: true,
  };

  const columns = [...baseColumns];
  if (canView || canEdit || canDelete) columns.push(actionColumn);

  return (
    <Routes>
      <Route path="/view/:id" element={<CostViewPage />} />

      <Route path="/" element={
        <div className="cost-administrative-pappers-page">
          <div className="cost-administrative-pappers-container">
            <h2 className='title'>COST AND ADMINISTRATIVE</h2>

            {canCreate && (
              <div className="add-button-container">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{ float: 'right', marginBottom: '40px' }}
                  onClick={() => setOpenAddModal(true)}
                >
                  New Cost
                </Button>
              </div>
            )}
          </div>

          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this record?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)}
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: '#424242',
                  '&:hover': {
                    backgroundColor: '#bdbdbd',
                  }
                }}
              >Cancel</Button>
              <Button
                color="error"
                onClick={async () => {
                  try {
                    await axiosInstance.delete(`/cost-administrative/${itemToDelete.id}/`);
                    fetchData();
                    setOpenDeleteDialog(false);
                  } catch (error) {
                    console.error('Error deleting record:', error.response?.data || error.message);
                  }
                }}
                sx={{
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  }
                }}
                autoFocus
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <div className="table-container">
            <DataGrid
              id="cost-administrative-table"
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "3px",
                  paddingBottom: "3px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>

          <AddCostModal
            open={openAddModal}
            onClose={() => setOpenAddModal(false)}
            onSave={handleAddCost}
          />

          <EditCostModal
            open={openEditModal}
            onClose={() => setOpenEditModal(false)}
            costData={selectedCost}
            onUpdate={handleCostUpdated}
          />
        </div>
      } />
    </Routes>
  );
}

export default CostControl;
